import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify"; // Assuming you use react-toastify for notifications
import {
    deleteFeedComment,
  getFeedComment,
  getFeeds,
  getMyFeeds,
  likeFeed,
  postFeedComment,
  postFeedCommentReplay,
  updateFeedCommentLikes,
  updateFeedCommentReplayLikes,
} from "../../../../Redux/Reducers/communityReducer";
import { useSelector } from "react-redux";

const useFeedActions = (postId, comments, setComments, feeds, setFeeds) => {
  const dispatch = useDispatch();
  const [isProcessing, setIsProcessing] = useState(false);
  const exploreType = useSelector((state) => state?.community?.exploreType);
   const updateCommentsCount = useCallback((feedId, increment) => {
    const feedIndex = feeds.findIndex((feed) => feed.id === feedId);
    if (feedIndex === -1) return;

    const updatedFeeds = [...feeds];
    updatedFeeds[feedIndex] = {
      ...updatedFeeds[feedIndex],
      comments_count: updatedFeeds[feedIndex].comments_count + (increment ? 1 : -1),
    };
    setFeeds(updatedFeeds);
  }, [feeds, setFeeds]);

  // Handle Comment Add with optimistic update
const handleSendComment = async (commentText) => {
  try {
    const res = await dispatch(postFeedComment({ 
      id: postId, 
      content: commentText 
    }));
    
    if (res?.payload?.statusCode === 201) {
      // Fetch latest comments to ensure consistency
      await dispatch(getFeedComment(postId));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error posting comment:', error);
    return false;
  }
};

  // Handle Reply Add with optimistic update
  const handleSendReply = useCallback(
    async (commentId, content) => {
      if (!content.trim()) return;

      // Optimistically update comments count
      updateCommentsCount(postId, true);

      setIsProcessing(true);
      try {
        const response = await dispatch(
          postFeedCommentReplay({
            id: postId,
            commentId,
            content: content,
          })
        ).unwrap();

        if (response.statusCode === 201) {
          await dispatch(getFeedComment(postId));
          return true;
        }
        // If failed, rollback the optimistic update
        updateCommentsCount(postId, false);
        throw new Error("Failed to post reply");
      } catch (error) {
        // Rollback optimistic update on error
        updateCommentsCount(postId, false);
        toast.error(error.message || "Failed to post reply");
        return false;
      } finally {
        setIsProcessing(false);
      }
    },
    [postId, dispatch, updateCommentsCount]
  );

  // Keep existing handleLike implementation
  const handleLike = useCallback(async () => {
    const feedIndex = feeds.findIndex((feed) => feed.id === postId);
    if (feedIndex === -1) return;

    const oldFeed = feeds[feedIndex];
    const newFeed = {
      ...oldFeed,
      has_liked: !oldFeed.has_liked,
      likes_count: oldFeed.has_liked
        ? oldFeed.likes_count - 1
        : oldFeed.likes_count + 1,
    };

    const updatedFeeds = [...feeds];
    updatedFeeds[feedIndex] = newFeed;
    setFeeds(updatedFeeds);

    try {
      const res = await dispatch(likeFeed(postId)).unwrap();

      if (res?.statusCode === 200) {
        const payload = { type: exploreType, my_posts: exploreType =="PERSONAL" ?  true:false };
        await Promise.all([
          dispatch(getMyFeeds({ type: exploreType })),
          dispatch(getFeeds(payload)),
        ]);
      } else {
        throw new Error("Like failed");
      }
    } catch (error) {
      const rollbackFeeds = [...feeds];
      rollbackFeeds[feedIndex] = oldFeed;
      setFeeds(rollbackFeeds);
      toast.error("Failed to like post");
    }
  }, [postId, feeds, dispatch, setFeeds]);



  // Handle Comment Like
  const handleLikeComment = useCallback(
    async (postId, commentId) => {
      const commentIndex = comments?.findIndex(
        (comment) => comment.id === commentId
      );
      if (commentIndex === -1) return;

      const oldComment = comments[commentIndex];
      const newComment = {
        ...oldComment,
        has_liked: !oldComment.has_liked,
        likes_count: oldComment.has_liked
          ? oldComment.likes_count - 1
          : oldComment.likes_count + 1,
      };

      // Optimistic update
      const updatedComments = [...comments];
      updatedComments[commentIndex] = newComment;
      setComments(updatedComments);

      try {
        const res = await dispatch(
          updateFeedCommentLikes({ id: postId, commentId })
        ).unwrap();

        if (res?.statusCode !== 200) {
          throw new Error("Like failed");
        }

        // Optional: refetch to sync data if necessary
        await dispatch(getFeedComment(postId));
      } catch (error) {
        // Rollback
        const rollback = [...comments];
        rollback[commentIndex] = oldComment;
        setComments(rollback);
        console.error("Error liking comment:", error);
      }
    },
    [comments, setComments, dispatch]
  );

  // Handle Reply Like
  const handleFeedCommentReplyLike = useCallback(
    async (commentId, replyId) => {
      const commentIndex = comments?.findIndex(
        (comment) => comment.id === commentId
      );
      if (commentIndex === -1) return;

      const oldComment = comments[commentIndex];
      const replyIndex = oldComment?.replies?.findIndex(
        (reply) => reply.id === replyId
      );
      if (replyIndex === -1) return;

      const oldReply = oldComment.replies[replyIndex];
      const newReply = {
        ...oldReply,
        has_liked: !oldReply.has_liked,
        likes_count: oldReply.has_liked
          ? oldReply.likes_count - 1
          : oldReply.likes_count + 1,
      };

      // Optimistic update
      const updated = [...comments];
      const updatedComment = { ...updated[commentIndex] };
      const updatedReplies = [...updatedComment.replies];
      updatedReplies[replyIndex] = newReply;
      updatedComment.replies = updatedReplies;
      updated[commentIndex] = updatedComment;
      setComments(updated);

      try {
        const response = await dispatch(
          updateFeedCommentReplayLikes({
            id: postId,
            commentId,
            replyId,
          })
        ).unwrap();

        if (response.statusCode !== 200) {
          throw new Error("Like Failed");
        }
        // Fetch latest comments/replies after like
        // await dispatch(getFeedComment(postId));

        await dispatch(getFeedComment(postId));
      } catch (error) {
        // Rollback on failure
        const rollback = [...comments];
        rollback[commentIndex] = oldComment;
        setComments(rollback);
        toast.error("Failed to like reply");
      }
    },
    [comments, postId, dispatch, setComments]
  );

  // Handle Comment Add
//   const handleSendComment = useCallback(
//     async (content) => {
//       if (!content.trim()) return;

//       setIsProcessing(true);
//       try {
//         const response = await dispatch(
//           postFeedComment({
//             id: postId,
//             content: content,
//           })
//         ).unwrap();

//         if (response.statusCode === 201) {
//           // Optimistically update comment count
//           const feedIndex = feeds.findIndex((feed) => feed.id === postId);
//           if (feedIndex !== -1) {
//             const updatedFeeds = [...feeds];
//             updatedFeeds[feedIndex] = {
//               ...updatedFeeds[feedIndex],
//               comments_count: updatedFeeds[feedIndex].comments_count + 1,
//             };
//             setFeeds(updatedFeeds);
//           }
//           await dispatch(getFeedComment(postId));
//           return true;
//         }
//         throw new Error("Failed to post comment");
//       } catch (error) {
//         toast.error(error.message || "Failed to post comment");
//         return false;
//       } finally {
//         setIsProcessing(false);
//       }
//     },
//     [postId, feeds, dispatch, setFeeds]
//   );

  // Handle Reply Add
//   const handleSendReply = useCallback(
//     async (commentId, content) => {
//       if (!content.trim()) return;

//       setIsProcessing(true);
//       try {
//         const response = await dispatch(
//           postFeedCommentReplay({
//             id: postId,
//             commentId,
//             content: content,
//           })
//         ).unwrap();

//         if (response.statusCode === 201) {
//           // Optimistically update comment count
//           const feedIndex = feeds.findIndex((feed) => feed.id === postId);
//           if (feedIndex !== -1) {
//             const updatedFeeds = [...feeds];
//             updatedFeeds[feedIndex] = {
//               ...updatedFeeds[feedIndex],
//               comments_count: updatedFeeds[feedIndex].comments_count + 1,
//             };
//             setFeeds(updatedFeeds);
//           }
//           await dispatch(getFeedComment(postId));
//           return true;
//         }
//         throw new Error("Failed to post reply");
//       } catch (error) {
//         toast.error(error.message || "Failed to post reply");
//         return false;
//       } finally {
//         setIsProcessing(false);
//       }
//     },
//     [postId, feeds, dispatch, setFeeds]
//   );

//   const handleLike = useCallback(async () => {
//     // Find current feed
//     console.log("Post ID:", postId);
//     const feedIndex = feeds.findIndex((feed) => feed.id === postId);
//     if (feedIndex === -1) return;

//     const oldFeed = feeds[feedIndex];

//     // Create optimistically updated feed
//     const newFeed = {
//       ...oldFeed,
//       has_liked: !oldFeed.has_liked,
//       likes_count: oldFeed.has_liked
//         ? oldFeed.likes_count - 1
//         : oldFeed.likes_count + 1,
//     };

//     // Update UI immediately
//     const updatedFeeds = [...feeds];
//     updatedFeeds[feedIndex] = newFeed;
//     setFeeds(updatedFeeds);

//     try {
//       const res = await dispatch(likeFeed(postId)).unwrap();

//       if (res?.statusCode === 200) {
//         // Refresh feeds in background
//         const payload = { type: "PROFESSIONAL", my_posts: false };
//         await Promise.all([
//           dispatch(getMyFeeds({ type: "PROFESSIONAL" })),
//           dispatch(getFeeds(payload)),
//         ]);
//       } else {
//         throw new Error("Like failed");
//       }
//     } catch (error) {
//       // Rollback on failure
//       const rollbackFeeds = [...feeds];
//       rollbackFeeds[feedIndex] = oldFeed;
//       setFeeds(rollbackFeeds);
//       toast.error("Failed to like post");
//     }
//   }, [postId, feeds, dispatch, setFeeds]);

  const handleDeleteCommentOptimistic = useCallback(
    async (commentId, setLocalCommentsCount) => {  // Add setLocalCommentsCount parameter
      const feedIndex = feeds.findIndex((feed) => feed.id === postId);
      if (feedIndex === -1) return;

      const oldFeed = feeds[feedIndex];
      const newFeed = {
        ...oldFeed,
        comments_count: Math.max(0, oldFeed.comments_count - 1),
      };

      // Update UI immediately
      const updatedFeeds = [...feeds];
      updatedFeeds[feedIndex] = newFeed;
      setFeeds(updatedFeeds);

      // Update local comment count
      setLocalCommentsCount(prev => Math.max(0, prev - 1));

      try {
        const response = await dispatch(
          deleteFeedComment({ id: postId, commentId })
        ).unwrap();

        if (response.statusCode === 200) {
          await dispatch(getFeedComment(postId));
          return true;
        }
        throw new Error("Failed to delete comment");
      } catch (error) {
        // Rollback on failure
        const rollbackFeeds = [...feeds];
        rollbackFeeds[feedIndex] = oldFeed;
        setFeeds(rollbackFeeds);
        
        // Rollback local comment count
        setLocalCommentsCount(prev => prev + 1);
        
        toast.error(error.message || "Failed to delete comment");
        return false;
      }
    },
    [postId, feeds, dispatch, setFeeds]
);

  return {
    isProcessing,
    handleLike,
    handleLikeComment,
    handleFeedCommentReplyLike,
    handleSendComment,
    handleSendReply,
    handleDeleteCommentOptimistic
  };
};

export default useFeedActions;
